import { h } from "@stencil/core";
import { getTheme } from "./theme-stylesheet";
import { removeElement } from "../../common/array";
const STYLE_TO_AVOID_FOUC = ":host,html{visibility:hidden !important}";
/**
 * It allows you to load a style sheet in a similar way to the
 * native LINK or STYLE tags, but assigning it a name so that
 * it can be reused in different contexts,
 * either in the Document or in a Shadow-Root.
 */
export class ChTheme {
    constructor() {
        this.loaded = false;
        this.attachStyleSheets = true;
        this.avoidFlashOfUnstyledContent = true;
        this.model = undefined;
        this.timeout = 10000;
    }
    #successThemes = [];
    el;
    attachStyleSheetsChanged() {
        this.#toggleAttachedModels();
    }
    modelChanged(_, oldModel) {
        // TODO: Make fully reactive the model property
        if (!oldModel) {
            this.#loadModel();
        }
    }
    /**
     * Event emitted when the theme has successfully loaded
     */
    themeLoaded;
    connectedCallback() {
        this.el.hidden = true;
        this.#loadModel();
    }
    #loadModel = async () => {
        if (!this.model || (Array.isArray(this.model) && this.model.length === 0)) {
            return;
        }
        const normalizeModel = this.#normalizeModel();
        const themePromises = normalizeModel.map(item => getTheme(item, this.timeout));
        Promise.allSettled(themePromises).then(results => {
            this.#successThemes = results
                .filter(result => result.status === "fulfilled")
                .map(result => result.status === "fulfilled" && result.value);
            this.#attachThemes();
            this.themeLoaded.emit({
                success: this.#successThemes.map(successTheme => successTheme.name)
            });
            this.loaded = true;
            const rejected = results.filter(result => result.status === "rejected");
            if (rejected.length > 0) {
                // eslint-disable-next-line no-console
                console.error("Failed to load themes:", rejected);
            }
        });
    };
    #normalizeModel = () => {
        const list = Array.isArray(this.model) ? this.model : [this.model];
        return list.map(item => {
            if (typeof item === "string") {
                return { name: item };
            }
            return item;
        });
    };
    #attachThemes = () => {
        const root = this.el.getRootNode();
        if (!(root instanceof Document || root instanceof ShadowRoot)) {
            return;
        }
        const normalizeModel = this.#normalizeModel();
        this.#successThemes.forEach(successTheme => {
            if (this.#mustAttachTheme(normalizeModel, successTheme) &&
                !root.adoptedStyleSheets.includes(successTheme.styleSheet)) {
                root.adoptedStyleSheets.push(successTheme.styleSheet);
            }
        });
    };
    #detachThemes = () => {
        const root = this.el.getRootNode();
        if (!(root instanceof Document || root instanceof ShadowRoot)) {
            return;
        }
        this.#successThemes.forEach(successTheme => {
            const themeIndex = root.adoptedStyleSheets.findIndex(adoptedStyleSheet => adoptedStyleSheet === successTheme.styleSheet);
            if (themeIndex > -1) {
                removeElement(root.adoptedStyleSheets, themeIndex);
            }
        });
    };
    #mustAttachTheme = (normalizedModel, theme) => {
        // TODO: "normalizedModel" should be a Set to reduce lookup times
        const themeItemModel = normalizedModel.find(item => item.name === theme.name);
        // TODO: What's the meaning of this condition?
        if (themeItemModel.url ||
            themeItemModel.styleSheet) {
            return themeItemModel.attachStyleSheet ?? this.attachStyleSheets;
        }
        // TODO: Why do we return `true` instead of `false`?
        return true;
    };
    #toggleAttachedModels = () => {
        if (!this.loaded || !this.model) {
            return;
        }
        // TODO: We should unify this condition to iterate only over the successful
        // themes using "themeItemModel.attachStyleSheet ?? this.attachStyleSheets".
        // For this, we should see the TODOs in `#mustAttachTheme` method
        if (this.attachStyleSheets) {
            this.#attachThemes();
        }
        else {
            this.#detachThemes();
        }
    };
    render() {
        return (this.avoidFlashOfUnstyledContent &&
            !this.loaded && h("style", { key: 'b8ba12bd173bd58e320fd97b3c9ea2e04fadd479' }, STYLE_TO_AVOID_FOUC));
    }
    static get is() { return "ch-theme"; }
    static get properties() {
        return {
            "attachStyleSheets": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Indicates whether the theme should be attached to the Document or\nthe ShadowRoot after loading.\nThe value can be overridden by the `attachStyleSheet` property of the model."
                },
                "attribute": "attach-style-sheets",
                "reflect": false,
                "defaultValue": "true"
            },
            "avoidFlashOfUnstyledContent": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to visually hide the contents of the root node while the control's\nstyle is not loaded."
                },
                "attribute": "avoid-flash-of-unstyled-content",
                "reflect": false,
                "defaultValue": "true"
            },
            "model": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ThemeModel | undefined | null",
                    "resolved": "ThemeItemBaseModel & { styleSheet: string; } | ThemeItemBaseModel & { url?: string; } | ThemeItemModel[] | string | string[]",
                    "references": {
                        "ThemeModel": {
                            "location": "import",
                            "path": "./theme-types",
                            "id": "src/components/theme/theme-types.ts::ThemeModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specify themes to load"
                },
                "attribute": "model",
                "reflect": false
            },
            "timeout": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "10000",
                    "resolved": "10000",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the time to wait for the requested theme to load."
                },
                "attribute": "timeout",
                "reflect": false,
                "defaultValue": "10000"
            }
        };
    }
    static get states() {
        return {
            "loaded": {}
        };
    }
    static get events() {
        return [{
                "method": "themeLoaded",
                "name": "themeLoaded",
                "bubbles": true,
                "cancelable": true,
                "composed": false,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when the theme has successfully loaded"
                },
                "complexType": {
                    "original": "ChThemeLoadedEvent",
                    "resolved": "ChThemeLoadedEvent",
                    "references": {
                        "ChThemeLoadedEvent": {
                            "location": "import",
                            "path": "./theme-types",
                            "id": "src/components/theme/theme-types.ts::ChThemeLoadedEvent"
                        }
                    }
                }
            }];
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "attachStyleSheets",
                "methodName": "attachStyleSheetsChanged"
            }, {
                "propName": "model",
                "methodName": "modelChanged"
            }];
    }
}
